/*
  Collection ranges (sections/collection-ranges.liquid).
  - The row of range cards scrolls with the arrows, the dots or a swipe.
  - Picking a card loads that collection's product grid section (Section Rendering API)
    and swaps it in, then moves the address to the collection's URL, so reload, share and
    the back button all land on the same collection. If the grid can't be loaded the link
    just opens normally.
*/
class CollectionRanges extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ranges-track]');
    this.prevButton = this.querySelector('[data-ranges-prev]');
    this.nextButton = this.querySelector('[data-ranges-next]');
    this.dotsContainer = this.querySelector('[data-ranges-dots]');
    this.links = Array.from(this.querySelectorAll('[data-ranges-link]'));
    this.titleElement = this.closest('.collection-ranges')?.querySelector('[data-ranges-title]');
    this.cache = new Map();
    this.currentPath = window.location.pathname;

    this.prevButton?.addEventListener('click', () => this.scrollByPage(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByPage(1));
    this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
    new ResizeObserver(() => this.buildDots()).observe(this.track);

    this.links.forEach((link) => {
      link.addEventListener('click', (event) => this.onLinkClick(event, link));
      // start loading on hover/touch so the products are usually ready by the click
      link.addEventListener('pointerenter', () => this.prefetch(link.href), { once: true });
      link.addEventListener('focus', () => this.prefetch(link.href), { once: true });
    });

    this.onPopState = this.onPopState.bind(this);
    window.addEventListener('popstate', this.onPopState);

    this.scrollToActive();
    this.buildDots();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  /* ---------- scrolling ---------- */

  scrollByPage(direction) {
    const step = Math.max(this.track.clientWidth - 80, 120);
    this.track.scrollBy({ left: direction * step, behavior: this.motion() });
  }

  scrollToActive() {
    // on the brand's main collection the row starts at the beginning; on a range it centres that range
    const active = this.querySelector('.collection-ranges__card.is-active');
    if (!active || active.hasAttribute('data-ranges-all')) return;
    const item = active.parentElement;
    this.track.scrollLeft = item.offsetLeft - (this.track.clientWidth - item.offsetWidth) / 2;
  }

  pageCount() {
    const { scrollWidth, clientWidth } = this.track;
    return clientWidth > 0 ? Math.ceil(scrollWidth / clientWidth - 0.02) : 1;
  }

  buildDots() {
    const pages = this.pageCount();
    this.classList.toggle('is-static', pages <= 1);
    if (this.dotsContainer && this.dotsContainer.childElementCount !== pages) {
      this.dotsContainer.replaceChildren(
        ...Array.from({ length: pages }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'collection-ranges__dot';
          dot.setAttribute('aria-label', `${index + 1} / ${pages}`);
          dot.addEventListener('click', () => this.scrollToPage(index));
          return dot;
        })
      );
    }
    this.update();
  }

  scrollToPage(index) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const pages = this.pageCount();
    const left = pages > 1 ? (max * index) / (pages - 1) : 0;
    this.track.scrollTo({ left, behavior: this.motion() });
  }

  queueUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const left = Math.abs(this.track.scrollLeft);
    if (this.prevButton) this.prevButton.disabled = left <= 2;
    if (this.nextButton) this.nextButton.disabled = left >= max - 2;

    const dots = this.dotsContainer ? Array.from(this.dotsContainer.children) : [];
    if (!dots.length) return;
    const current = max > 0 ? Math.round((left / max) * (dots.length - 1)) : 0;
    dots.forEach((dot, index) => dot.setAttribute('aria-current', index === current ? 'true' : 'false'));
  }

  motion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /* ---------- loading a collection ---------- */

  gridSection() {
    return document.querySelector('.shopify-section.collection-section');
  }

  sectionUrl(href) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', this.gridSection().id.replace('shopify-section-', ''));
    return url.toString();
  }

  fetchGrid(href) {
    const key = this.sectionUrl(href);
    if (!this.cache.has(key)) {
      const request = fetch(key).then((response) => {
        if (!response.ok) throw new Error(`${response.status}`);
        return response.text();
      });
      request.catch(() => this.cache.delete(key));
      this.cache.set(key, request);
    }
    return this.cache.get(key);
  }

  prefetch(href) {
    if (this.gridSection()) this.fetchGrid(this.withSort(href)).catch(() => {});
  }

  // keep the chosen sort order when moving between ranges
  withSort(href) {
    const url = new URL(href, window.location.origin);
    const sort = new URL(window.location.href).searchParams.get('sort_by');
    if (sort) url.searchParams.set('sort_by', sort);
    return url.toString();
  }

  onLinkClick(event, link) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!this.gridSection()) return;
    event.preventDefault();
    if (link.classList.contains('is-active') && !link.classList.contains('is-loading')) return;
    this.load(this.withSort(link.href), link, true);
  }

  onPopState() {
    if (window.location.pathname === this.currentPath) return;
    const link = this.links.find((item) => new URL(item.href).pathname === window.location.pathname);
    if (link && this.gridSection()) {
      this.load(window.location.href, link, false);
    } else {
      window.location.reload();
    }
  }

  setActive(link) {
    this.links.forEach((item) => {
      const isActive = item === link;
      item.classList.toggle('is-active', isActive);
      if (isActive) {
        item.setAttribute('aria-current', 'page');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  async load(href, link, pushHistory) {
    const grid = this.gridSection();
    const url = new URL(href, window.location.origin);
    this.currentPath = url.pathname;
    this.loadingLink = link;
    this.setActive(link);
    link.classList.add('is-loading');
    grid.classList.add('is-ranges-loading');
    if (pushHistory) history.pushState({ collectionRanges: true }, '', url.pathname + url.search);

    try {
      const html = await this.fetchGrid(href);
      // a later pick won: drop this one
      if (this.loadingLink !== link) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (!fresh) throw new Error('no section');
      grid.innerHTML = fresh.innerHTML;

      const title = link.dataset.title || fresh.querySelector('.collection__title')?.textContent.trim();
      if (this.titleElement && title) this.titleElement.textContent = title;
      if (link.dataset.pageTitle) document.title = link.dataset.pageTitle;
      const crumb = document.querySelector('.breadcrumbs .breadcrumbs--last');
      if (crumb && title) crumb.textContent = title;

      document.dispatchEvent(new CustomEvent('collection:rerendered'));
    } catch (error) {
      window.location.href = url.toString();
      return;
    } finally {
      if (this.loadingLink === link) {
        link.classList.remove('is-loading');
        grid.classList.remove('is-ranges-loading');
      }
    }
  }
}

if (!customElements.get('collection-ranges')) customElements.define('collection-ranges', CollectionRanges);
